/**
 * Canonical list of rodeo event types.
 *
 * Source: product-owner notes ("Rodeo Events" handwritten page, attached to
 * bug CV2UuktcxxoS). Drives the `event_category` and `barrel_racing` flags
 * the wizard writes onto `events.metadata` at event creation.
 *
 * Draw-order semantics (per the attached notes):
 *   - scored events: highest priority is typically LAST out
 *   - timed events (non-barrel): highest priority is typically LAST out
 *   - Barrel Racing (timed, `barrel_racing=true`): highest priority is FIRST out
 *
 * Novice variants are duplicates of every base discipline with a `Novice `
 * prefix, appended to the bottom of the list. They share scoring/draw
 * semantics with their base — `Novice Barrel Racing` keeps `isBarrelRacing`.
 * Use `getBaseEventType()` to normalise a label back to its base discipline
 * before any equality check that should treat the two variants the same.
 */

export type RodeoEventCategory = 'scored' | 'timed';

export interface RodeoEventType {
  /** Display label shown in dropdowns and on cards. */
  label: string;
  /** Scoring model — lowest-time-wins vs. highest-score-wins. */
  category: RodeoEventCategory;
  /**
   * True only for Barrel Racing (and `Novice Barrel Racing`). Flips the
   * highest-priority-first-out rule for timed events. Maps 1:1 to
   * `events.metadata.barrel_racing`.
   */
  isBarrelRacing?: boolean;
  /** True for `Novice <Base>` rows. Lets callers style/group novice entries. */
  isNovice?: boolean;
  /** The base discipline label this entry maps to (self for non-novice rows). */
  baseLabel: string;
}

const NOVICE_PREFIX = 'Novice ';

const BASE_RODEO_EVENT_TYPES: ReadonlyArray<Omit<RodeoEventType, 'baseLabel' | 'isNovice'>> = [
  { label: 'Bareback Riding', category: 'scored' },
  { label: 'Saddle Bronc Riding', category: 'scored' },
  { label: 'Ranch Bronc Riding', category: 'scored' },
  { label: 'Bull Riding', category: 'scored' },
  { label: 'Tie-Down Roping', category: 'timed' },
  { label: 'Breakaway Roping', category: 'timed' },
  { label: 'Steer Wrestling', category: 'timed' },
  { label: 'Team Roping', category: 'timed' },
  { label: 'Barrel Racing', category: 'timed', isBarrelRacing: true },
];

const baseEntries: RodeoEventType[] = BASE_RODEO_EVENT_TYPES.map((e) => ({
  ...e,
  baseLabel: e.label,
}));

const noviceEntries: RodeoEventType[] = BASE_RODEO_EVENT_TYPES.map((e) => ({
  ...e,
  label: `${NOVICE_PREFIX}${e.label}`,
  isNovice: true,
  baseLabel: e.label,
}));

export const RODEO_EVENT_TYPES: readonly RodeoEventType[] = [
  ...baseEntries,
  ...noviceEntries,
];

/** Names-only list, preserving the canonical order for dropdowns. */
export const RODEO_EVENT_TYPE_NAMES: readonly string[] = RODEO_EVENT_TYPES.map(
  (e) => e.label,
);

/** Lookup helper — case-insensitive match against the canonical label. */
export function findRodeoEventType(name: string | null | undefined): RodeoEventType | undefined {
  if (!name) return undefined;
  const needle = name.trim().toLowerCase();
  return RODEO_EVENT_TYPES.find((e) => e.label.toLowerCase() === needle);
}

/**
 * Strip the `Novice ` prefix from an event-type label so callers can compare
 * a novice variant against its base discipline. Whitespace and underscore/
 * dash separators are normalised to a single space; the returned string
 * preserves the original casing for the remainder of the label so it stays
 * usable as a display value.
 *
 * Examples:
 *   getBaseEventType('Novice Team Roping') === 'Team Roping'
 *   getBaseEventType('team_roping')        === 'team roping'
 *   getBaseEventType(undefined)            === ''
 */
export function getBaseEventType(name: string | null | undefined): string {
  if (typeof name !== 'string') return '';
  const collapsed = name.replace(/[_\-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!collapsed) return '';
  if (collapsed.toLowerCase().startsWith(NOVICE_PREFIX.toLowerCase())) {
    return collapsed.slice(NOVICE_PREFIX.length).trim();
  }
  return collapsed;
}

/** True iff `name` is a `Novice <Base>` variant of the canonical list. */
export function isNoviceEventType(name: string | null | undefined): boolean {
  if (typeof name !== 'string') return false;
  return name.replace(/[_\-]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase().startsWith(
    NOVICE_PREFIX.toLowerCase(),
  );
}

/**
 * Resolved scoring model for a single event, used by the scoring UI to decide
 * which penalty controls to render and which sort direction to apply.
 *
 *   - timed events  → lowest final time wins; penalties add seconds.
 *   - scored events → highest score wins; judges hand a final score to the
 *     secretary, so there are NO penalty controls (per product owner).
 *
 * Penalty controls differ by discipline (all per the product-owner notes):
 *   - Team Roping (timed)  → Barrier + Leg (+5 for roping a leg).
 *   - Barrel Racing (timed)→ Barrels knocked over, +5 each, up to 3.
 *   - other timed events   → Barrier only.
 */
export interface EventScoringModel {
  category: RodeoEventCategory;
  isTimed: boolean;
  isScored: boolean;
  /** Base discipline label, e.g. 'Team Roping' (novice prefix stripped). */
  baseLabel: string;
  isBarrelRacing: boolean;
  isTeamRoping: boolean;
}

/**
 * Derive the scoring model from an event row. `events.metadata.event_category`
 * (written by the wizard) is authoritative; we fall back to the canonical
 * event-type table keyed off `trig_event_type_name` when metadata is absent
 * (e.g. legacy events). Jackpots — which have no discipline layer — resolve to
 * timed Team Roping, preserving the existing jackpot behaviour.
 */
/**
 * Decimal places used when entering / displaying a TIMED-event time. Barrel
 * Racing is timed to the thousandth of a second (3 decimals); every other timed
 * event stays at hundredths (2). Scored (judged) events format their own point
 * scores and never reach here, but a `false` flag safely yields 2.
 */
export function timedScoreDecimals(isBarrelRacing: boolean): number {
  return isBarrelRacing ? 3 : 2;
}

export function resolveEventScoring(
  event:
    | {
        metadata?: Record<string, unknown> | null;
        trig_event_type_name?: string | null;
      }
    | null
    | undefined,
): EventScoringModel {
  const meta = (event?.metadata ?? {}) as Record<string, unknown>;
  const typeName = (event?.trig_event_type_name as string) || '';
  const typeDef = findRodeoEventType(typeName);
  const base = getBaseEventType(typeName) || typeDef?.baseLabel || '';
  const baseLower = base.toLowerCase();

  let category: RodeoEventCategory = 'timed';
  const metaCat = meta.event_category;
  if (metaCat === 'timed' || metaCat === 'scored') category = metaCat;
  else if (typeDef) category = typeDef.category;

  const isBarrelRacing =
    meta.barrel_racing === true || !!typeDef?.isBarrelRacing || baseLower === 'barrel racing';
  const isTeamRoping = baseLower === 'team roping';

  return {
    category,
    isTimed: category === 'timed',
    isScored: category === 'scored',
    baseLabel: base,
    isBarrelRacing,
    isTeamRoping,
  };
}
